import api from './api';
import { ApiResponse } from '../types';
import {
  FeeAssignmentListFilter,
  FeeAssignmentListResponse,
  FeeAssignmentRow,
  BulkAssignPreview,
  BulkAssignResult,
} from '../types/fees';

/**
 * Thin service for /api/fee-assignments. The bulk-assign preview and commit
 * are separate endpoints so the UI can show a confirmation summary before
 * it commits — same pattern the promotion-run flow uses.
 */

const buildListParams = (filter: FeeAssignmentListFilter = {}) => {
  const sp = new URLSearchParams();
  if (filter.sessionId) sp.append('sessionId', filter.sessionId);
  if (filter.classId)   sp.append('classId', filter.classId);
  if (filter.sectionInstanceId === null)  sp.append('sectionInstanceId', 'null');
  else if (filter.sectionInstanceId)      sp.append('sectionInstanceId', filter.sectionInstanceId);
  if (filter.status)    sp.append('status', filter.status);
  if (filter.feePlanId) sp.append('feePlanId', filter.feePlanId);
  if (filter.studentId) sp.append('studentId', filter.studentId);
  if (filter.search)    sp.append('search', filter.search);
  if (filter.page)      sp.append('page', String(filter.page));
  if (filter.limit)     sp.append('limit', String(filter.limit));
  return sp;
};

export const listFeeAssignments = async (filter: FeeAssignmentListFilter = {}): Promise<FeeAssignmentListResponse> => {
  const sp = buildListParams(filter);
  const response = await api.get<ApiResponse<FeeAssignmentListResponse>>(`/fee-assignments?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const getFeeAssignment = async (id: string): Promise<FeeAssignmentRow> => {
  const response = await api.get<ApiResponse<FeeAssignmentRow>>(`/fee-assignments/${id}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const previewBulkAssign = async (input: {
  planId: string;
  /** Multi-select. When both are set, `classIds` wins. */
  classIds?: string[] | null;
  classId?: string | null;
  sectionInstanceId?: string | null;
}): Promise<BulkAssignPreview> => {
  const response = await api.post<ApiResponse<BulkAssignPreview>>('/fee-assignments/preview', input);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const bulkAssign = async (input: {
  planId: string;
  classIds?: string[] | null;
  classId?: string | null;
  sectionInstanceId?: string | null;
  skipExisting?: boolean;
}): Promise<BulkAssignResult> => {
  const response = await api.post<ApiResponse<BulkAssignResult>>('/fee-assignments/bulk', input);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const closeFeeAssignment = async (id: string, reason?: string): Promise<void> => {
  const response = await api.post<ApiResponse>(`/fee-assignments/${id}/close`, { reason });
  if (!response.data.success) throw new Error(response.data.message);
};

const feeAssignmentService = {
  listFeeAssignments,
  getFeeAssignment,
  previewBulkAssign,
  bulkAssign,
  closeFeeAssignment,
};

export default feeAssignmentService;
